import { usePlayer } from '@/composables/game/player'
import { computed, ref } from 'vue'
import api from '@/services/api'
import type { Skin } from '@/types/skin'
import { SKIN_CATEGORIES } from '@/types/skin'
import { useConfig } from '@/composables/game/config'
import {
  ECOMMERCE_COLLECTIBLE_CATEGORY,
  ECOMMERCE_COLLECTIBLE_TYPE,
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  ECOMMERCE_ITEM_TAG,
} from '@/types/shared/player-config'
import { useToaster } from '@/composables/game/toasters'
import { useTranslate } from '@tolgee/vue'
import { useEcommerceItems } from '@/composables/game/ecommerce'

export const useSkin = () => {
  const { player, updatePlayer } = usePlayer()
  const { config } = useConfig()
  const { notify } = useToaster()
  const { t } = useTranslate()
  const { ecommerceItems } = useEcommerceItems()

  const skinShopItems = computed(() => ecommerceItems.value.filter((item) =>
    item.benefits.find(benefit =>
      benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.COLLECTIBLE && benefit.collectibleType === ECOMMERCE_COLLECTIBLE_TYPE.TAP_SKIN,
    ),
  ))

  const skins = computed<Skin[]>(() => {
    const skinList: Skin[] = []
    skinShopItems.value.filter((item) => Object.keys(config.value.game.ecommerce.collectibles).includes(item.id)).map((skinItem) =>
      skinList.push({
        id: skinItem.id,
        name: skinItem.name,
        description: skinItem.description,
        imageUrl: config.value.game.ecommerce.collectibles[skinItem.id]?.assetUrl,
        price: skinItem.prices,
        category: (skinItem.benefits[0] as
            {
              type: ECOMMERCE_ITEM_BENEFIT_TYPE.COLLECTIBLE,
              amount: number,
              collectibleId: string,
              collectibleType: ECOMMERCE_COLLECTIBLE_TYPE,
              category: ECOMMERCE_COLLECTIBLE_CATEGORY
            }
        ).category as string as SKIN_CATEGORIES ?? SKIN_CATEGORIES.BASIC,
      }),
    )
    return skinList
  })

  const canHaveSale = computed(() => skinShopItems.value.some((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT)))

  const basicSkins = computed(() => skins.value.filter((skin) => skin.category === SKIN_CATEGORIES.BASIC))
  const premiumSkins = computed(() => skins.value.filter((skin) => skin.category === SKIN_CATEGORIES.PREMIUM))
  const epicSkins = computed(() => skins.value.filter((skin) => skin.category === SKIN_CATEGORIES.EPIC))

  const ownedSkinIds = computed(() => player.value.ecommerce.collectibles.ownedIds)
  const currentSkinId = computed(() => player.value.ecommerce.collectibles.tap.currentSkinId ?? skins.value[0].id)
  const currentSkin = computed(() => getSkinById(currentSkinId.value) ?? skins.value[0])
  const loadingSkin = ref(false)

  function getSkinById(skinId: string) {
    return skins.value.find((item) => item.id === skinId)
  }

  function getSkinShopItemById(skinId: string) {
    return config.value.game.ecommerce.items.find((item) => item.id === skinId)
  }

  function hasSkinDiscount(skinId: string) {
    return skinShopItems.value.findIndex((item) => item.id === skinId && item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT)) >= 0
  }

  async function selectSkin(skinId: string) {
    try {
      loadingSkin.value = true
      const { playerStats } = await api.useCollectible({ collectibleId: skinId })
      await updatePlayer(playerStats)
      loadingSkin.value = false
      notify({ title: t.value('skin.updated', 'Skin updated!'), type: 'success' })
    } catch (e) {
      console.error(e)
      loadingSkin.value = false
    }
  }

  return {
    currentSkin,
    ownedSkinIds,
    selectSkin,
    getSkinById,
    getSkinShopItemById,
    skins,
    basicSkins,
    premiumSkins,
    epicSkins,
    loadingSkin,
    canHaveSale,
    hasSkinDiscount,
  }
}
